/* heon-swatch.js: metal groups in the product gallery, on the product page and in the quick shop (swatch team, 2026-09-26).
   Moved here from assets/heon-pdp.js (its part 7) so the quick shop gets it too: snippets/heon-pdp-gallery.liquid prints the
   script tag and quick-add.js copies the page's script tags when a quick shop opens on a collection or search page.
   Photos come from heon-pdp-gallery.liquid with data-hp-i (their place in the product's media) and, on each metal's own photo,
   data-hp-metal; <media-gallery data-hp-metals="Yellow Gold|White Gold|Rose Gold|"> lists every metal of the option.
   - A metal with photos of its own shows them first, then the shared ones (on-body photos, size chart, group shots); the
     first metal keeps the product's own order (phone audit P3-11). A clasp close-up naming one metal belongs to that metal.
   - A metal with no photo of its own (CEO, 2026-09-26: a swatch must never leave the other metal's photos on screen) shows a
     photo of the three metals together first when there is one ("14K yellow, white and rose gold ..."), then the first
     metal's photos. Before, choosing it kept whatever metal was on screen.
   - Photos out of view leave the tab order (perf-a11y P2-11). Not when every photo is in view: the product page's photo
     grid from 1024px (pdp3 round 3, .media-viewer shown as a grid) keeps all of them in the tab order (pdp3, 2026-09-26).
   - In that grid the chosen variant's own photo (a stone, a metal) comes first, then its metal's photos in their usual
     order: a slider scrolls to the photo, a grid shows them all, so the photo moves (pdp3, 2026-09-26, recolor team's
     report: another stone kept the first stone's photo on top). snippets/heon-pdp-gallery.liquid prints the same order,
     so nothing moves on load. A choice with an option still open (a ring's size) has no variant: the photo is the first
     variant's with the chosen values (data-hp-v); the grid leads with it, the phone slider goes to it.
   - A variant with no photo of its own (the cartilage studs' Bow and Bar) shows the photo snippets/heon-media-fallback
     picked for it (media-gallery data-hp-fb): the same look in the chosen metal, else a photo of every look. Before, the
     metal's first photo stayed, another style's (qa3 #1: "Single - Bow" in white gold showed the white Cross; polish,
     2026-09-26).
   - A product video (pdpvideo, 2026-09-29, CEO: second, right after the cover photo) belongs to every metal and always comes
     right after the group's first photo, also when the grid leads with the chosen variant's photo. heon-pdp-gallery prints
     the same order. */
(() => {
  const ours = g => !!g.querySelector('.media-viewer__item[data-hp-i]');
  const METAL = /\b(yellow|rose|white)(?:\s+and\s+(yellow|rose|white))?\s+gold\b/gi;
  const TRIO = /\b(yellow|white|rose),?\s+(yellow|white|rose),?\s+and\s+(yellow|white|rose)\s+gold\b/i;
  // theme i18n (2026-09-28): on a page in another language each photo carries its English words (data-hh-alt, from
  // snippets/heon-altmap: "~clasp~#my~" = a clasp close-up naming yellow gold, #t the three metals together) and the
  // gallery the metals' English first words (data-hh-mw, in the order of data-hp-metals); English pages read the alt
  const altOf = li => li.hasAttribute('data-hh-alt') ? li.dataset.hhAlt : li.querySelector('img')?.getAttribute('alt') || '';
  const isTrio = a => a.charAt(0) === '~' ? a.includes('~#t~') : TRIO.test(a);
  function metalsIn(alt) {
    if (alt.charAt(0) === '~') return [...new Set((alt.match(/~#m[ywr](?=~)/g) || []).map(f => ({ y: 'yellow', w: 'white', r: 'rose' })[f.charAt(3)]))];
    const found = new Set();
    for (const m of alt.matchAll(METAL)) { found.add(m[1].toLowerCase()); if (m[2]) found.add(m[2].toLowerCase()); }
    return [...found];
  }
  // the videos right after the first photo (pdpvideo)
  const isVid = li => li.dataset.mediaType === 'video';
  function vid2(list) {
    const vids = list.filter(isVid), rest = list.filter(li => !isVid(li));
    return vids.length && rest.length ? [rest[0]].concat(vids, rest.slice(1)) : list;
  }
  function groupMap(g) {
    const items = [...g.querySelectorAll('.media-viewer__item[data-hp-i]')].sort((a, b) => a.dataset.hpI - b.dataset.hpI);
    const anchors = items.filter(li => li.dataset.hpMetal);
    if (!anchors.length) return null;
    const listed = (g.dataset.hpMetals || '').split('|').filter(Boolean);
    const names = [...new Set(listed.concat(anchors.map(li => li.dataset.hpMetal)))];
    if (names.length < 2) return null;
    const own = new Map(names.map(n => [n, []]));
    const clasps = new Map(names.map(n => [n, []]));
    const mw = g.dataset.hhMw ? g.dataset.hhMw.split('|') : null;
    const byWord = new Map(names.map(n => [mw && listed.indexOf(n) > -1 ? mw[listed.indexOf(n)] : n.toLowerCase().split(' ')[0], n]));
    const shared = [];
    items.forEach(li => {
      if (isVid(li)) { shared.push(li); return; }
      if (li.dataset.hpMetal) { own.get(li.dataset.hpMetal).push(li); return; }
      const alt = altOf(li);
      const ms = metalsIn(alt);
      if (/clasp/i.test(alt) && ms.length === 1 && byWord.has(ms[0])) { clasps.get(byWord.get(ms[0])).push(li); return; }
      shared.push(li);
    });
    // a metal's clasp close-up comes after its own photos: the rope chain's rose clasp (media 7) opened the rose group
    // before the rose chain (media 9) in the quick shop (qa3 #10; polish, 2026-09-26). heon-pdp-gallery prints the same order.
    names.forEach(n => own.set(n, own.get(n).concat(clasps.get(n))));
    const first = anchors[0].dataset.hpMetal;
    const firstList = own.get(first).concat(shared).sort((a, b) => a.dataset.hpI - b.dataset.hpI);
    const thumbs = g.querySelector('.media-thumbs');
    const map = { groups: {} };
    names.forEach(n => {
      let list;
      if (n === first) list = firstList;
      else if (own.get(n).length) list = own.get(n).concat(shared);
      else {
        // no photo of its own: the three metals together first, then the first metal's photos
        // (any three-metal photo, also one that the data ties to another metal's variants)
        const trio = items.filter(li => isTrio(altOf(li)));
        list = trio.concat(firstList.filter(li => !trio.includes(li)));
      }
      list = vid2(list);
      map.groups[n] = {
        name: n,
        items: list.map(li => ({ main: li, thumb: thumbs ? thumbs.querySelector(`.media-thumbs__item[data-media-id="${li.dataset.mediaId}"]`) : null }))
      };
    });
    map.groupFromItem = item => Object.values(map.groups).find(gr => gr.items.some(it => it.main === item)) || map.groups[names[0]];
    return map;
  }
  // links and buttons in photos out of view leave the tab order
  function tabs(g) {
    const cur = g.currentItem;
    const v = g.viewer || g.querySelector('.media-viewer');
    const grid = !!v && getComputedStyle(v).display === 'grid';   // every photo in view: all stay reachable
    g.querySelectorAll('.media-viewer__item').forEach(li => {
      const on = grid || li === cur;
      li.querySelectorAll('a[href], button, [tabindex], video, iframe').forEach(el => {
        if (on) {
          if (el.hasAttribute('data-hp-tab')) {
            const t = el.dataset.hpTab;
            if (t) el.setAttribute('tabindex', t); else el.removeAttribute('tabindex');
            el.removeAttribute('data-hp-tab');
          }
        } else if (!el.hasAttribute('data-hp-tab')) {
          el.dataset.hpTab = el.getAttribute('tabindex') || '';
          el.setAttribute('tabindex', '-1');
        }
      });
    });
  }
  // the photo grid: the chosen variant's photo (g.hpLead) first, then the group in its order, hidden photos last
  const isGrid = g => !!g.viewer && getComputedStyle(g.viewer).display === 'grid';
  function lead(g) {
    if (!isGrid(g)) return;
    const v = g.viewer, shown = li => li.style.display !== 'none';
    if (g.hpLead === undefined) g.hpLead = v.querySelector('.media-viewer__item.is-current-variant');
    const gr = g.currentGroup;
    let list = gr && gr.items ? gr.items.map(it => it.main).filter(shown)
      : [...v.querySelectorAll('.media-viewer__item')].filter(shown).sort((a, b) => a.dataset.hpI - b.dataset.hpI);
    if (g.hpLead && list.includes(g.hpLead)) list = [g.hpLead].concat(list.filter(li => li !== g.hpLead));
    list = vid2(list);
    const now = [...v.children].filter(li => li.matches('.media-viewer__item') && shown(li));
    if (now.length === list.length && now.every((li, i) => li === list[i])) return;
    list.forEach(li => v.appendChild(li));
    [...v.children].filter(li => li.matches('.media-viewer__item') && !shown(li)).forEach(li => v.appendChild(li));
    g.setVisibleItems();
  }
  function regroup(g, group) {
    const viewer = g.viewer;
    if (!viewer || !group) return;
    group.items.forEach(it => viewer.appendChild(it.main));
    [...viewer.children].filter(li => li.matches('.media-viewer__item') && li.style.display === 'none').forEach(li => viewer.appendChild(li));
    const th = group.items.map(it => it.thumb).filter(Boolean);
    if (th.length) {
      const list = th[0].parentNode;
      th.forEach(t => list.appendChild(t));
      [...list.children].filter(t => t.style.display === 'none').forEach(t => list.appendChild(t));
    }
    g.setVisibleItems();
    g.currentItem = null;
    g.hpRegrouping = true;
    try { g.customSetActiveMedia(group.items[0].main, true, false); } finally { g.hpRegrouping = false; }
  }
  customElements.whenDefined('media-gallery').then(() => {
    const proto = customElements.get('media-gallery').prototype;
    if (proto.hpPatched) return;
    proto.hpPatched = true;
    const getMap = proto.getMediaGroupMap;
    proto.getMediaGroupMap = function () {
      if (!this.mediaGroupMap && ours(this)) {
        const m = groupMap(this);
        if (m) { this.mediaGroupMap = m; this.viewerItems = this.querySelectorAll('.media-viewer__item'); }
      }
      return getMap.call(this);
    };
    const setGroup = proto.setActiveMediaGroup;
    proto.setActiveMediaGroup = function (name) {
      const before = this.currentGroup;
      setGroup.call(this, name);
      if (ours(this) && this.currentGroup && this.currentGroup !== before) regroup(this, this.currentGroup);
    };
    const setMedia = proto.customSetActiveMedia;
    proto.customSetActiveMedia = function (...args) {
      const out = setMedia.apply(this, args);
      if (ours(this)) {
        // a variant's photo (the theme's variant change, a thumbnail); not a swipe (scrollToItem false) or a regroup
        if (args[0] && args[1] !== false && !this.hpRegrouping) this.hpLead = args[0];
        lead(this);
        tabs(this);
      }
      return out;
    };
    // galleries built before this ran (the product page's own, a quick shop opened while this loaded): group them again
    document.querySelectorAll('media-gallery').forEach(g => {
      if (!ours(g) || !g.viewer) return;
      if (g.mediaGroupingEnabled) {
        g.mediaGroupMap = null;
        g.currentGroup = null;
        try { g.setActiveMediaGroup(g.getMediaGroupFromOptionSelectors()); } catch (err) { /* keep the theme's grouping */ }
      }
      lead(g);
      tabs(g);
    });
    // options chosen but no variant yet (a ring's size still open): the first variant with the chosen values shows its
    // photo, as a full variant does (the theme's own handler on the section has run by the time this bubbles here).
    // A variant with no photo of its own: the photo data-hp-fb names for it (polish, 2026-09-26)
    document.addEventListener('on:variant:change', e => {
      const d = e.detail, sel = d && d.selectedOptions;
      if (!d) return;
      if (d.variant ? d.variant.featured_media : !Array.isArray(sel) || !sel.some(x => x != null)) return;
      const sec = e.target.closest && e.target.closest('.js-product');
      const g = sec && sec.querySelector('media-gallery');
      if (!g || !g.viewer || !ours(g)) return;
      let li;
      if (d.variant) {
        const hit = (g.dataset.hpFb || '').split(',').find(p => p.split(':')[0] === String(d.variant.id));
        li = hit && g.viewer.querySelector(`.media-viewer__item[data-media-id="${hit.split(':')[1]}"]`);
        if (li && li.style.display === 'none') li = null;
      } else {
        li = [...g.viewer.querySelectorAll('.media-viewer__item[data-hp-v]')].find(l => {
          if (l.style.display === 'none') return false;
          const o = l.dataset.hpV.split(' / ');
          return sel.every((x, i) => x == null || o[i] === x);
        });
      }
      if (!li) return;
      if (isGrid(g)) { g.hpLead = li; lead(g); tabs(g); } else g.customSetActiveMedia(li, true);
    });
    // the photo grid comes and goes at 1024px: tab order again when the window crosses it
    matchMedia('(min-width:1024px)').addEventListener('change', () => {
      document.querySelectorAll('media-gallery').forEach(g => { if (ours(g)) { lead(g); tabs(g); } });
    });
  });
})();
